import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useApiClient } from '../api/client';
import { VoiceCallDetail, VoiceRecording } from '../api/voiceCalls';
import { InfoTile, PageHeader, PageShell, StatusBadge } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';
import { voiceCallDate, voiceCallDuration, voiceCallJson } from '../utils/voiceCalls';
import './VoiceCalls.css';

export function VoiceCallDetailsPage() {
  const { id = '' } = useParams();
  const { hash } = useLocation();
  const { request } = useApiClient();
  const { t, language } = useI18n();
  const [response, setResponse] = React.useState<{ id: string; value: VoiceCallDetail } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);
  const data = response?.id === id ? response.value : null;
  React.useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true); setError(false); setResponse(null);
    request<VoiceCallDetail>(`/admin/voice-calls/${encodeURIComponent(id)}`, { method: 'GET', signal: controller.signal })
      .then((value) => { if (active) setResponse({ id, value }); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [id, refresh, request]);
  React.useEffect(() => {
    if (!data || !hash) return;
    let target: string;
    try { target = decodeURIComponent(hash.slice(1)); } catch { return; }
    const element = document.getElementById(target);
    if (element instanceof HTMLDetailsElement) {
      element.open = true;
      element.scrollIntoView?.({ block: 'nearest' });
    }
  }, [data, hash]);
  const call = data?.call;
  const messages = (data?.messages || []).filter((message) => ['user', 'assistant', 'bot'].includes(message.role) && message.text)
    .slice().sort((a, b) => a.seq - b.seq);
  const tools = (data?.toolCalls || []).slice().sort((a, b) => (Date.parse(a.at || '') || 0) - (Date.parse(b.at || '') || 0));
  return <PageShell>
    <Link className="admin-user-back-link" to="/voice-calls">{t('voiceCalls.back')}</Link>
    <PageHeader title={t('voiceCalls.details')} description={t('voiceCalls.description')}
      actions={<button className="btn btn-neutral" onClick={() => setRefresh((v) => v + 1)}>{t('action.refresh')}</button>} />
    {loading && <p role="status" className="voice-empty">{t('voiceCalls.loadingDetail')}</p>}
    {error && <p role="alert" className="voice-alert">{t('voiceCalls.detailError')}</p>}
    {call && data && <>
      <section className="admin-panel voice-overview">
        <div className="voice-identity"><div><div className="voice-id">{call.id}</div>
          <div className="mt-2">{call.userId && <Link className="voice-link mr-3" to={`/users/${encodeURIComponent(call.userId)}`}>{call.userFirstName || call.userId}</Link>}{call.customerNumber || t('voiceCalls.unknownCaller')}</div></div>
          <StatusBadge tone={call.reportReceived ? 'green' : 'amber'}>{t(call.reportReceived ? 'voiceCalls.complete' : 'voiceCalls.pending')}</StatusBadge>
        </div>
        <div className="voice-facts">
          <InfoTile label={t('voiceCalls.started')} value={voiceCallDate(call.startedAt, language)} />
          <InfoTile label={t('voiceCalls.duration')} value={voiceCallDuration(call.startedAt, call.endedAt)} />
          <InfoTile label={t('voiceCalls.endedReason')} value={call.endedReason || '—'} />
          <InfoTile label={t('voiceCalls.tools')} value={<>{call.toolCalls} <span className="voice-muted">({t('voiceCalls.failedCount', { count: call.failedTools })})</span></>} />
          <InfoTile label={t('voiceCalls.userTurns')} value={call.userTurns} />
          <InfoTile label={t('voiceCalls.bookings')} value={call.bookings} />
        </div>
      </section>
      <RecordingPlayer key={`${id}-${refresh}`} id={id} />
      <div className="voice-detail-grid">
        <section className="admin-panel voice-conversation" aria-label={t('voiceCalls.conversation')}>
          <h2 className="voice-section-title">{t('voiceCalls.conversation')}</h2>
          {messages.length ? <ol className="voice-messages">{messages.map((message) => <li key={message.seq} className={`voice-message voice-message-${message.role}`}>
            <header><strong>{t(message.role === 'user' ? 'voiceCalls.role.user' : 'voiceCalls.role.assistant')}</strong>
              <time>{message.secondsFromStart != null ? `+${message.secondsFromStart.toFixed(1)} s` : voiceCallDate(message.time, language)}</time></header>
            <p>{message.text}</p>
          </li>)}</ol> : call.transcript ? <p className="voice-transcript">{call.transcript}</p> : <p className="voice-empty">{t('voiceCalls.noConversation')}</p>}
        </section>
        <section className="admin-panel voice-tools" aria-label={t('voiceCalls.tools')}>
          <h2 className="voice-section-title">{t('voiceCalls.tools')} <span className="voice-muted">{tools.length}</span></h2>
          {!tools.length && <p className="voice-empty">{t('voiceCalls.noTools')}</p>}
          <div className="voice-tool-list">{tools.map((tool) => <details key={tool.toolCallId} id={`tool-${tool.toolCallId}`} className="voice-tool">
            <summary><span className="voice-tool-name">{tool.name || tool.toolCallId}</span><span className="voice-tool-status">
              <StatusBadge tone={tool.ok === false ? 'red' : tool.ok === true ? 'green' : 'slate'}>{t(tool.ok === false ? 'voiceCalls.failed' : tool.ok === true ? 'voiceCalls.succeeded' : 'voiceCalls.unknownStatus')}</StatusBadge>
              <span>{tool.ms != null ? `${tool.ms} ms` : '—'}</span></span></summary>
            <div className="voice-tool-content"><div className="voice-tool-meta"><span className="voice-id">{tool.toolCallId}</span><time>{voiceCallDate(tool.at, language)}</time>
              <Link className="voice-link" to={`/voice-calls/${encodeURIComponent(id)}#tool-${encodeURIComponent(tool.toolCallId)}`}>{t('voiceCalls.toolLink')}</Link></div>
              <h3>{t('voiceCalls.arguments')}</h3><pre>{voiceCallJson(tool.arguments)}</pre>
              <h3>{t('voiceCalls.result')}</h3><pre>{voiceCallJson(tool.result)}</pre>
            </div>
          </details>)}</div>
        </section>
      </div>
      <details className="admin-panel voice-metadata"><summary>{t('voiceCalls.metadata')}</summary>
        <dl className="voice-facts">
          {[['orgId', call.orgId], ['assistantId', call.assistantId], ['assistantVersion', call.assistantVersion], ['type', call.type],
            [t('voiceCalls.cost'), call.cost], [t('voiceCalls.model'), call.model], [t('voiceCalls.voice'), call.voice], [t('voiceCalls.transcriber'), call.transcriber]].map(([label, value]) =>
            <div key={String(label)}><dt>{String(label)}</dt><dd><pre>{voiceCallJson(value)}</pre></dd></div>)}
        </dl>
      </details>
    </>}
  </PageShell>;
}

function RecordingPlayer({ id }: { id: string }) {
  const { request } = useApiClient();
  const { t, language } = useI18n();
  const [recording, setRecording] = React.useState<VoiceRecording | null>(null);
  const [track, setTrack] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);
  const [error, setError] = React.useState<'load' | 'playback' | null>(null);
  const active = React.useRef(true);
  const controller = React.useRef<AbortController | null>(null);
  React.useEffect(() => {
    active.current = true;
    return () => { active.current = false; controller.current?.abort(); };
  }, []);
  const load = async () => {
    controller.current?.abort();
    const current = new AbortController(); controller.current = current;
    setLoading(true); setAttempted(true); setError(null); setRecording(null); setTrack('');
    try {
      const value = await request<VoiceRecording>(`/admin/voice-calls/${encodeURIComponent(id)}/recording`, { method: 'GET', signal: current.signal });
      if (!active.current || current.signal.aborted) return;
      const links = Object.fromEntries(Object.entries(value.links || {}).filter(([, url]) => {
        try { return new URL(url).protocol === 'https:'; } catch { return false; }
      })) as VoiceRecording['links'];
      setRecording({ ...value, links }); setTrack(['combined', 'caller', 'assistant', 'stereo'].find((key) => links[key as keyof VoiceRecording['links']]) || '');
    } catch {
      if (active.current && !current.signal.aborted) setError('load');
    } finally {
      if (active.current && !current.signal.aborted) setLoading(false);
    }
  };
  const entries = (['combined', 'caller', 'assistant', 'stereo'] as const).filter((key) => recording?.links[key]);
  const src = recording?.links[track as keyof VoiceRecording['links']];
  return <section className="admin-panel voice-recording" aria-label={t('voiceCalls.recording')}>
    <div className="voice-recording-heading"><div><h2>{t('voiceCalls.recording')}</h2><p className="voice-muted">{t('voiceCalls.recordingHint')}</p></div>
      <button type="button" className="btn btn-neutral" disabled={loading} onClick={load}>{t(loading ? 'voiceCalls.loadingRecording' : attempted ? 'voiceCalls.reloadRecording' : 'voiceCalls.loadRecording')}</button>
    </div>
    {error && <p role="alert" className="voice-alert">{t(error === 'playback' ? 'voiceCalls.playbackError' : 'voiceCalls.recordingError')}</p>}
    {recording && !entries.length && <p className="voice-muted mt-3">{t('voiceCalls.noRecording')}</p>}
    {src && <div className="voice-audio">
      {entries.length > 1 && <label>{t('voiceCalls.recordingTrack')} <select className="form-control" value={track} onChange={(event) => { setTrack(event.target.value); setError(null); }}>{entries.map((key) => <option value={key} key={key}>{t(`voiceCalls.track.${key}`)}</option>)}</select></label>}
      <audio key={src} controls preload="none" src={src} aria-label={t('voiceCalls.recording')} onError={() => setError('playback')} />
      {recording?.expiresAt && <p className="voice-muted">{t('voiceCalls.expires', { time: voiceCallDate(recording.expiresAt, language) })}</p>}
    </div>}
  </section>;
}
